import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { FC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown, ArrowLink } from "components";
import { Carousel3EmployeeContent } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Carousel3EmployeeContent;
}

const Employee: FC<Props> = withErrorBoundary(
  ({
    className = "",
    content: {
      name,
      experience,
      description,
      linkLabel,
      carousel3Link,
      photo: {
        data: { photo },
      },
    },
  }) => {
    return (
      <StyledEmployee className={className}>
        <div className="Carousel3__photoWithTextContainer">
          <LegacyImage
            {...photo}
            layout="fill"
            className="Carousel3__photo"
            objectFit="cover"
            objectPosition="center"
          />
          <div className="Carousel3__nameContainer">
            <p className="Font_18_26_700">{name}</p>
            <p className="Carousel3__experience Font_14_20_450">{experience}</p>
          </div>
        </div>
        <Markdown className="Carousel3__description Font_18_26_450 Color_secondary">{description}</Markdown>
        {carousel3Link && linkLabel && (
          <ArrowLink href={carousel3Link} className="Carousel3__link Font_18_26_600 Color_secondary">
            {linkLabel}
          </ArrowLink>
        )}
      </StyledEmployee>
    );
  },
  {}
);

const StyledEmployee = styled.div`
  .Carousel3__photoWithTextContainer {
    position: relative;
    width: 100%;
    height: 300px;
    display: flex;
    align-items: flex-end;
    padding: 24px;
    overflow: hidden;
  }

  .Carousel3__photo {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    z-index: 0;
  }

  .Carousel3__nameContainer {
    position: relative;
    display: flex;
    flex-direction: column;
    color: ${({ theme }) => theme.colors.fog["00"]};
    z-index: 1;
  }

  .Carousel3__experience {
    margin-top: 4px;
  }

  .Carousel3__description {
    margin-top: 40px;
  }

  .Carousel3__link {
    margin-top: 12px;

    svg {
      fill: ${({ theme }) => theme.colors.fog["80"]};
    }
  }

  @media (max-width: 860px) {
    .Carousel3__photoWithTextContainer {
      height: 228px;
      padding: 16px 16px 9px;
    }

    .Carousel3__experience {
      margin-top: -2px;
    }

    .Carousel3__description {
      margin-top: 24px;
    }

    .Carousel3__link {
      margin-top: 24px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Carousel3__photoWithTextContainer {
      height: 234px;
    }
  }
`;

gql`
  fragment Carousel3EmployeeContent on ComponentNestedCarousel3Persons {
    id
    name
    experience
    description
    carousel3Link
    linkLabel
    photo {
      data {
        photo: attributes {
          src: hash
          alt: alternativeText
        }
      }
    }
  }
`;

export { Employee };
